import Phaser from "phaser";
import { WalkChapter } from "./WalkChapter";
import { BOMBEROS } from "../data/bomberos";
import type { ChapterData } from "../data/content-types";
import type { InteractableDef } from "../core/types";
import { isoDepth, TILE_H } from "../core/iso";
import * as P from "../art/props";

/** CASO EXTRA — "Cuestión de minutos". Nave con riesgos de incendio: conato, salida bloqueada, sobrecarga. */
export class BomberosScene extends WalkChapter {
  protected chapter: ChapterData = BOMBEROS;
  protected grid = { w: 15, h: 12 };
  protected spawn = { x: 8, y: 10 };
  protected worldSigns = [
    { id: "f_extintor", x: 1.4, y: 0.3 },
    { id: "e_salida", x: 5.5, y: 0.3 },
    { id: "f_bie", x: 9.5, y: 0.3 },
    { id: "f_pulsador", x: 13.4, y: 0.3 },
  ];
  private decor: Phaser.GameObjects.GameObject[] = [];
  private fireFx: Phaser.GameObjects.Arc[] = [];
  private blockCrates: Phaser.GameObjects.Container[] = [];

  constructor() {
    super("bomberos");
  }

  protected floorKind(x: number, y: number): P.TileKind {
    const h = (x * 928371 + y * 12347) & 255;
    return h % 15 === 0 ? "dirt" : "concrete";
  }

  /** Un conato (papelera ardiendo) dibujado con vectores. */
  private fire(): Phaser.GameObjects.Container {
    const c = this.add.container(0, 0);
    const g = this.add.graphics();
    // papelera metálica
    g.fillStyle(0x565c62, 1); g.beginPath(); g.moveTo(-11, 0); g.lineTo(11, 0); g.lineTo(9, -18); g.lineTo(-9, -18); g.closePath(); g.fillPath();
    g.fillStyle(0x3f454b, 1); g.fillEllipse(0, -18, 18, 5);
    // llamas
    g.fillStyle(0xd8501a, 0.95); g.beginPath(); g.moveTo(-8, -16); g.lineTo(-2, -40); g.lineTo(3, -22); g.lineTo(9, -44); g.lineTo(8, -16); g.closePath(); g.fillPath();
    g.fillStyle(0xf0a91b, 0.95); g.beginPath(); g.moveTo(-4, -18); g.lineTo(1, -34); g.lineTo(5, -22); g.lineTo(6, -18); g.closePath(); g.fillPath();
    g.fillStyle(0xffe36a, 0.9); g.beginPath(); g.moveTo(-1, -20); g.lineTo(2, -30); g.lineTo(4, -20); g.closePath(); g.fillPath();
    c.add(g);
    return c;
  }

  protected buildInteractableArt(def: InteractableDef): Phaser.GameObjects.GameObject[] {
    switch (def.id) {
      case "conato": return [this.fire()];
      case "extintor": return [P.extinguisher(this)];
      case "salidaEmerg": return [P.gate(this)];
      case "material": return [P.crateStack(this)];
      case "bie": return [P.crate(this, 0xb23a2a)];
      case "medidas": return [P.machine(this, 0xf0a91b)];
      default: return super.buildInteractableArt(def);
    }
  }

  protected buildBackdrop(): void {
    const cam = this.cameras.main;
    cam.setBackgroundColor(0x8c847a);
    const W = 1280, H = 720, over = 420;
    const sky = this.add.graphics().setScrollFactor(0).setDepth(-19000);
    sky.fillGradientStyle(0xa39a8d, 0xa39a8d, 0x847c70, 0x847c70, 1);
    sky.fillRect(-over, -over, W + over * 2, H * 0.58 + over);
    // cerchas del techo de la nave
    const roof = this.add.graphics().setScrollFactor(0.25).setDepth(-18900);
    roof.lineStyle(2, 0x6a6560, 0.7);
    const ry = H * 0.2;
    for (let cx = 40; cx < W; cx += 150) {
      roof.beginPath(); roof.moveTo(cx, ry); roof.lineTo(cx + 75, ry - 28); roof.lineTo(cx + 150, ry); roof.strokePath();
    }
    roof.lineStyle(3, 0x6a6560, 0.55); roof.beginPath(); roof.moveTo(0, ry); roof.lineTo(W, ry); roof.strokePath();
    this.buildWalls({ left: 0x93887b, right: 0xa89c8c, height: 176, base: 0x655c50, texture: "metal" });
  }

  protected buildDecor(): void {
    this.decor.forEach((d) => d.destroy());
    this.decor = [];
    const put = (obj: Phaser.GameObjects.Container, x: number, y: number): void => {
      const p = this.iso(x, y);
      obj.setPosition(p.sx, p.sy).setDepth(isoDepth(x, y));
      this.decor.push(obj);
    };

    // cajas obstruyendo la salida de emergencia (12,3) — la trampa
    this.blockCrates = [];
    const bc1 = P.crateStack(this); put(bc1, 11.4, 3.2);
    const bc2 = P.crate(this, 0xa9843f); put(bc2, 12.0, 3.6);
    this.blockCrates = [bc1, bc2];
    // material combustible amontonado (9,7) junto a la estufa/cuadro
    put(P.pallet(this), 9.5, 6.5);
    put(P.crateStack(this), 8.6, 7.4);
    // palés tapando la BIE (12,6)
    put(P.pallet(this), 12.6, 5.6);

    // resto de nave (densidad)
    put(P.shelving(this), 1.6, 1.4);
    put(P.shelving(this), 13.0, 1.4);
    put(P.barrel(this, 0xc4632a), 2.4, 8.6);
    put(P.barrel(this, 0x2f6b3f), 2.9, 8.9);
    put(P.sacks(this), 6.2, 8.6);
    put(P.toolbox(this, 0x2f6b8f), 6.6, 3.2);
    put(P.cone(this), 6.4, 6.0);
    put(P.cone(this), 10.2, 8.4);
    put(P.fence(this), 4.4, 8.6);
    put(P.warningSign(this), 4.6, 5.2);

    // luminarias colgantes + charco de luz
    for (const [lx, ly] of [[3.5, 3], [8.5, 3], [12, 3], [6.5, 6.5], [10.5, 6.5]] as [number, number][]) {
      const p = this.iso(lx, ly);
      const pg = this.add.graphics().setDepth(-9300); const pcy = p.sy + TILE_H / 2;
      pg.fillStyle(0xffe6a0, 0.13); pg.fillEllipse(p.sx, pcy, 80, 38);
      this.decor.push(pg); this.addPulse(pg, 0.12, 0.004, 0.86);
      const lamp = P.hanglamp(this, 150); lamp.setPosition(p.sx, p.sy).setDepth(50000); this.decor.push(lamp); this.addSway(lamp);
    }

    // conato (7,5): llamas parpadeantes + humo que sube (si no está resuelto)
    this.fireFx = [];
    if (!this.state.chapter("bomberos").done) {
      this.fireFx = [
        ...this.addRisers(7, 5, { count: 5, color: 0xf0a91b, alpha: 0.9, r: 2.2, rise: 24, sp: 0.02, grow: 0.5, dyBase: -14, depth: 45000 }),
        ...this.addRisers(7, 5, { count: 4, color: 0x3a3630, alpha: 0.28, r: 5, rise: 48, sp: 0.006, grow: 1.8, dyBase: -22, depth: 46000 }),
      ];
    }
    // sobrecarga de la regleta (3,7): chispa ocasional
    this.addRisers(3, 7, { count: 2, color: 0xfff2a0, alpha: 0.85, r: 1.2, rise: 12, sp: 0.03, grow: 0.2, dyBase: -6, depth: 44000 });

    if (this.state.chapter("bomberos").done) this.onResolved();
  }

  /** Camión de bomberos (mirando a la izquierda). */
  private firetruck(): Phaser.GameObjects.Container {
    const c = this.add.container(0, 0);
    const g = this.add.graphics();
    g.fillStyle(0x1a1206, 0.2); g.fillEllipse(0, 6, 78, 17);
    g.fillStyle(0xc4122e, 1); g.fillRoundedRect(-40, -32, 80, 28, 4);       // caja roja
    g.fillStyle(0x9a0d22, 1); g.fillRoundedRect(-40, -16, 80, 12, 3);       // banda baja
    g.fillStyle(0xf3f4f6, 1); g.fillRect(-40, -20, 80, 4);                  // franja blanca
    g.fillStyle(0xbfe0ee, 1); g.fillRoundedRect(-39, -30, 13, 12, 2);       // luna
    g.lineStyle(2, 0xd9d9de, 1); g.beginPath(); g.moveTo(-10, -30); g.lineTo(34, -34); g.strokePath(); // escalera
    g.fillStyle(0x2b3038, 1); g.fillCircle(-22, -3, 6.5); g.fillCircle(22, -3, 6.5);
    g.fillStyle(0x0d0f12, 1); g.fillCircle(-22, -3, 2.6); g.fillCircle(22, -3, 2.6);
    c.add(g);
    const light = this.add.circle(4, -38, 4, 0x3a7bd8, 1).setName("light");
    c.add(light);
    return c;
  }

  /** Efectos: apagar el conato (y llega el camión) y despejar la salida de emergencia. */
  protected attendedEffect(ref: string, live: boolean): void {
    if (ref === "conato") {
      this.fireFx.forEach((c) => c.destroy()); this.fireFx = [];
      if (live) {
        this.addRisers(7, 5, { count: 4, color: 0xcfd4d8, alpha: 0.4, r: 5, rise: 40, sp: 0.01, grow: 2, dyBase: -16, depth: 46000 }); // bocanada de humo al apagar
        this.ui.toast("Bomberos", "El parque manda una dotación", "good");
        const truck = this.firetruck();
        const entry = this.iso(15, 10.5), near = this.iso(9, 6), exit = this.iso(16, 3.5);
        truck.setPosition(entry.sx, entry.sy).setDepth(80000);
        const lt = truck.getByName("light") as Phaser.GameObjects.Arc;
        if (lt) this.tweens.add({ targets: lt, alpha: 0.2, yoyo: true, repeat: -1, duration: 220 });
        this.tweens.add({
          targets: truck, x: near.sx, y: near.sy, duration: 1500, ease: "Sine.easeInOut",
          onComplete: () => this.time.delayedCall(1600, () => {
            this.tweens.add({ targets: truck, x: exit.sx, y: exit.sy, alpha: 0, duration: 1700, ease: "Sine.easeIn", onComplete: () => truck.destroy() });
          }),
        });
      }
    }
    if (ref === "salidaEmerg") {
      this.blockCrates.forEach((c) => {
        if (live) this.tweens.add({ targets: c, alpha: 0, x: c.x + 34, duration: 500, ease: "Sine.easeIn", onComplete: () => c.destroy() });
        else c.destroy();
      });
      this.blockCrates = [];
      const p = this.iso(12, 3);
      this.add.text(p.sx, p.sy - 40, "SALIDA LIBRE", { fontSize: "9px", color: "#fff", backgroundColor: "#2f8f57", padding: { x: 4, y: 2 } }).setOrigin(0.5).setDepth(71);
    }
  }

  protected onResolved(): void {
    // salida despejada + plan activo
    const s = this.iso(12, 3);
    this.add.text(s.sx, s.sy - 40, "SALIDA LIBRE", { fontSize: "9px", color: "#fff", backgroundColor: "#2f8f57", padding: { x: 4, y: 2 } }).setOrigin(0.5).setDepth(71);
    const m = this.iso(8, 9);
    this.add.text(m.sx, m.sy - 40, "PLAN ACTIVO · SIMULACRO OK", { fontSize: "9px", color: "#241d14", backgroundColor: "#f0c400", padding: { x: 4, y: 2 } }).setOrigin(0.5).setDepth(71);
  }
}
